import { Question } from '../types/form';
import { MoldOrder } from '../types/mold';
import { getNestedValue } from '../utils/object';
import { isFinalStep } from '../components/MoldDetail/utils';
import { Section } from '../types/form';

/**
 * Determines if a question is answered NG
 * @param question - The question object
 * @param formData - The form data object
 * @returns True if the question is answered NG, false otherwise
 */
export function isAnsweredNG(question: Question, formData: Record<string, any>): boolean {
  return question.type === 'radio' && formData[question.id] === 'NG';
}

/**
 * Determines the initial value for a radio question from an entity relation value.
 * Handles boolean mapping and option matching.
 * @param entityValue - The value from the entity relation
 * @param options - The question's options array
 * @returns The initial value for the radio question, or '' if not matched
 */
export function getInitialRadioValue(entityValue: any, options: { value: string }[]): string {
  if (typeof entityValue === 'boolean') {
    return entityValue ? 'yes' : 'no';
  } else {
    const matchingOption = options.find(option => option.value === entityValue);
    if (matchingOption) {
      return entityValue;
    }
  }
  return '';
}

/**
 * Parses entity values for checkbox-group questions into an array of option values.
 * Handles array, single string, and comma-separated string formats.
 * For checkbox groups, entity value could be:
 * 1. A single value string that matches an option
 * 2. An array of values
 * 3. A comma-separated string of values
 * @param entityValue - The value to parse
 * @returns Array of option values
 */
export function parseCheckboxEntityValues(entityValue: any): string[] {
  if (Array.isArray(entityValue)) {
    return entityValue;
  } else if (typeof entityValue === 'string') {
    return entityValue.includes(',')
      ? entityValue.split(',').map(v => v.trim())
      : [entityValue];
  }
  return [];
}

/**
 * Counts the number of radio questions answered as 'NG' in the form data.
 * @param sections - Array of Section objects
 * @param formData - The form data object
 * @returns Number of NG answers
 */
export function countNgAnswers(sections: Section[], formData: Record<string, any>): number {
  let count = 0;
  sections.forEach(section => {
    section.questions.forEach(question => {
      if (question.type === 'radio' && formData[question.id] === 'NG') {
        count++;
      }
    });
  });
  return count;
}

/**
 * Fetches a value from a MoldOrder or its mold entity based on type and field name.
 * Used for entity relation logic in questionnaire forms.
 * @param moldOrder - The mold order object
 * @param entityType - The type of entity ('mold', 'mold_order')
 * @param fieldName - The field name to fetch
 * @returns The value or null
 */
export function fetchEntityValue(moldOrder: MoldOrder, entityType: string, fieldName: string): any {
  try {
    switch (entityType) {
      case 'mold':
        if (moldOrder?.mold) {
          // Use getNestedValue for deep field access
          // getNestedValue is imported below
          return getNestedValue(moldOrder.mold, fieldName);
        }
        return null;
      case 'mold_order':
        if (moldOrder) {
          return getNestedValue(moldOrder, fieldName);
        }
        return null;
      default:
        console.warn(`Unknown entity type: ${entityType}`);
        return null;
    }
  } catch (error) {
    console.error(`Failed to fetch ${fieldName} from ${entityType}:`, error);
    return null;
  }
}

export const getEntitiesByType = (moldOrder: MoldOrder, entityType: string) => {
    switch (entityType) {
        case 'insert':
        case 'inserts':
            return moldOrder?.mold?.inserts || [];
        case 'part':
        case 'parts':
            return moldOrder?.mold?.productParts || [];
        default:
            return [];
    }
};

export const getAllEntityRelations = (moldOrder: MoldOrder, entityType: string, questionId?: string) => {
    // If mold order is in final step, prioritize form data but show mold entities if form is empty
    if (isFinalStep(moldOrder.status) && questionId) {
        // Get entities from form data structure using questionId
        // The form data structure is: moldOrder.orderDetail.form[questionId][entityId][fieldId] = value
        const formEntityData = moldOrder?.orderDetail?.form?.[questionId] || {};
        const formEntityIds = Object.keys(formEntityData);
        
        // If form has entity data, use it
        if (formEntityIds.length > 0) {
            const formEntities = formEntityIds.map(entityId => {
                const entityData = formEntityData[entityId];
                return {
                    id: parseInt(entityId),
                    ...entityData,
                    _removed: false
                };
            });
            
            return {
                allEntities: formEntities,
                removedEntities: [],
            };
        }
        
        // If form is empty, show mold entities (they will be populated into form by initializeFormStateWithEntityValues)
        const moldEntities = getEntitiesByType(moldOrder, entityType) || [];
        return {
            allEntities: moldEntities.map(entity => ({ ...entity, _removed: false })),
            removedEntities: [],
        };
    }
    
    // For non-final steps, use the original logic (rely on current mold data only)
    // Get current entities from moldOrder
    const entities = getEntitiesByType(moldOrder, entityType) || [];
    // Get all entity ids from current entities
    const entityIds = entities.map((e: any) => e.id);

    // Find all entity ids that have images, using orderDetail.form.entityRelations.{entityType}
    // Expecting prop: moldOrder.orderDetail.form.entityRelations[entityType] = [{ id, images: { ... } }, ...]
    let imageEntityIdSet = new Set<string | number>();
    const entityRelations = moldOrder?.orderDetail?.form?.entityRelations?.[entityType];
    if (Array.isArray(entityRelations)) {
      entityRelations.forEach((rel: any) => {
        if (rel && rel.id !== undefined && rel.id !== null) {
          imageEntityIdSet.add(rel.id);
        }
      });
    }

    // Find removed entity ids (have images but not in current entity list)
    const removedEntityIds = Array.from(imageEntityIdSet).filter(id => !entityIds.includes(id));

    // Build removed entity objects (with only id)
    const removedEntities = removedEntityIds.map(id => ({ id, _removed: true }));

    // Merge current and removed entities for display
    const allEntities = [
      ...entities.map(e => ({ ...e, _removed: false })),
      ...removedEntities,
    ];

    return {
      allEntities,
      removedEntities,
    };
}

// Helper to generate initial form state
export const generateInitialFormState = (sections: Section[] | undefined) => {
  const initialState: Record<string, any> = {};
  if (!Array.isArray(sections)) {
    return initialState;
  }
  sections.forEach(section => {
    if (!section.questions || !Array.isArray(section.questions)) return;
    section.questions.forEach(question => {
      switch (question.type) {
        case 'radio':
          initialState[question.id] = '';
          break;
        case 'checkbox-group':
          initialState[question.id] = question.options.reduce((acc, option) => ({
            ...acc,
            [option.value]: false
          }), {});
          break;
        case 'entity-list':
          // Initialize with empty structure for file tracking
          initialState[question.id] = {};
          break;
        case 'details': {
          // Use DetailFieldConfig directly, which now includes 'default'
          initialState[question.id] = Object.keys(question.fields).reduce((acc: Record<string, any>, field: string) => {
            const fieldConfig = question.fields[field];
            if (fieldConfig && fieldConfig.type === 'date' && fieldConfig.default === 'today') {
              acc[field] = new Date().toISOString().slice(0, 10);
            } else if (fieldConfig && typeof fieldConfig.default !== 'undefined') {
              acc[field] = fieldConfig.default;
            } else {
              acc[field] = '';
            }
            return acc;
          }, {});
          break;
        }
      }
    });
  });
  return initialState;
};


/*******************************************************
 *                     UI related                      *
 *******************************************************/
export const getOptionColor = (optionValue: string, isSelected: boolean) => {
  // Default styling for non yes/no options
  let baseColors = {
    border: 'border-gray-200',
    bg: 'bg-white',
    hoverBorder: 'hover:border-blue-400',
    hoverBg: 'hover:bg-blue-50/50',
    selectedBorder: 'border-blue-500',
    selectedBg: 'bg-blue-50',
    selectedText: 'text-blue-700'
  };

  // Special styling for yes/no and OK/NG options
  if (optionValue.toLowerCase() === 'ok') {
    baseColors = {
      border: 'border-green-200',
      bg: 'bg-green-50/50',
      hoverBorder: 'hover:border-green-400',
      hoverBg: 'hover:bg-green-50',
      selectedBorder: 'border-green-500',
      selectedBg: 'bg-green-100',
      selectedText: 'text-green-500'
    };
  } else if (optionValue.toLowerCase() === 'ng') {
    baseColors = {
      border: 'border-red-200',
      bg: 'bg-red-50/50',
      hoverBorder: 'hover:border-red-400',
      hoverBg: 'hover:bg-red-50',
      selectedBorder: 'border-red-500',
      selectedBg: 'bg-red-50',
      selectedText: 'text-red-700'
    };
  }

  return isSelected
    ? `${baseColors.selectedBorder} ${baseColors.selectedBg} ${baseColors.selectedText}`
    : `${baseColors.border} ${baseColors.bg} ${baseColors.hoverBorder} ${baseColors.hoverBg}`;
};

export const getRadioColor = (optionValue: string, isSelected: boolean) => {
  if (optionValue.toLowerCase() === 'ok') {
    return isSelected ? 'checked:border-green-500' : 'border-green-300';
  } else if (optionValue.toLowerCase() === 'ng') {
    return isSelected ? 'checked:border-red-500' : 'border-red-300';
  }
  return isSelected ? 'checked:border-blue-500' : 'border-gray-300';
};

/**
 * Maps the number of options to a Tailwind grid-cols class for radio questions.
 */
export function getRadioGridColsClass(optionsCount: number): string {
  switch (optionsCount) {
    case 1: return 'sm:grid-cols-1';
    case 2: return 'sm:grid-cols-2';
    case 3: return 'sm:grid-cols-3';
    case 4: return 'sm:grid-cols-4';
    case 5: return 'sm:grid-cols-5';
    case 6: return 'sm:grid-cols-6';
    default: return 'sm:grid-cols-1';
  }
}

/**
 * The REAL formData side-keys for a conditional attachment (B3). Mirrors the keys
 * written by ConditionalAttachmentChoice (orderDetailKey override, else the default
 * `${qId}PhotoAttachments`/`${qId}FileAttachments`).
 */
export function getConditionalAttachmentOrderDetailKeys(question: Question): {
  photoOrderDetailKey: string;
  fileOrderDetailKey: string;
} {
  const cfg = question.conditionalAttachment?.attachmentConfig;
  return {
    photoOrderDetailKey:
      cfg?.photoConfig?.orderDetailKey || `${question.id}PhotoAttachments`,
    fileOrderDetailKey:
      cfg?.fileConfig?.orderDetailKey || `${question.id}FileAttachments`,
  };
}